import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { TrackingCategory, USER_INFO_UPDATE } from '@/entities/tracking/const';
import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';
import { FullUser } from '@/graphql/__generated/resolvers-types';
import { getUserInfo } from '@/utils/tracking/getUserInfo';

import { cacheKey as fullUserProfileCacheKey } from '../providers/useFullUser';

export const useTrackUserInfoUpdate = (): void => {
    const queryClient = useQueryClient();
    const hasMarkedRef = useRef(BootstrapSignals.has(BootstrapSignalKeys.USER_INFO_UPDATE));
    const lastEmittedUserInfoRef = useRef<string | null>(null);

    useEffect(() => {
        if (hasMarkedRef.current) return;
        if (!queryClient) return;

        const queryCache = queryClient.getQueryCache();

        const emitForData = (data: FullUser | undefined) => {
            const userInfo = getUserInfo(data);
            const emit = () => {
                // Skip unchanged passive refetches, which can reset an idle Orion session (DV-41).
                const serializedUserInfo = JSON.stringify(userInfo);
                if (lastEmittedUserInfoRef.current === serializedUserInfo) return;
                lastEmittedUserInfoRef.current = serializedUserInfo;
                window.adobeDataLayer.push({ userInfo });
                void TrackerEngine.update({
                    type: USER_INFO_UPDATE,
                    detail: { userInfo },
                    category: TrackingCategory.App.OPERATION,
                });
                if (!hasMarkedRef.current) {
                    hasMarkedRef.current = true;
                    BootstrapSignals.mark(BootstrapSignalKeys.USER_INFO_UPDATE);
                }
            };

            if (hasMarkedRef.current) {
                emit();
            } else {
                void BootstrapSignals.whenAll([BootstrapSignalKeys.APP_INIT]).then(emit);
            }
        };

        // Cover the SSR-prefetch / hydration case: the query may already be in `success` state
        // by the time this subscriber mounts (most pages prefetch fullUserProfile in
        // getServerSideProps). Without this, anon and logged-in users hit the 15s pageview gate
        // timeout because the subscriber only sees future refetch events.
        const existingQueries = queryCache.findAll({ queryKey: [fullUserProfileCacheKey] });
        const successQuery = existingQueries.find((q) => q.state.status === 'success');
        if (successQuery) {
            const cachedData = successQuery.state.data as
                | { userFullProfile?: FullUser }
                | undefined;
            emitForData(cachedData?.userFullProfile);
        }

        const unsubscribe = queryCache.subscribe((event) => {
            const query = event?.query;
            const key = query?.queryKey?.[0];
            if (key !== fullUserProfileCacheKey) return;

            const isFetchOrRefetchSuccess =
                event.type === 'updated' && event.action?.type === 'success';
            if (!isFetchOrRefetchSuccess || query?.state?.status !== 'success') return;

            const data = query?.state?.data?.userFullProfile as FullUser | undefined;
            emitForData(data);
        });

        return () => unsubscribe();
    }, [queryClient]);
};
